<template>
  <div ref="wrap" class="table-wrap" :class="{ 'table-wrap--cards': cards, 'table-wrap--refreshing': refreshing }">
    <!-- The skeleton rows below are aria-hidden, so without this a screen
         reader hears nothing at all while the data is on its way. -->
    <p v-if="loading" class="sr-only" role="status">{{ uiLabel('loading') }}</p>

    <table ref="tableEl" class="table" :class="{ 'table--tight': tight }" :aria-busy="loading || undefined">
      <thead>
        <tr>
          <th v-if="selectable" class="table__tick-head">
            <UiCheckbox
              :model-value="allPicked"
              :indeterminate="somePicked && !allPicked"
              :aria-label="uiLabel('selectAll')"
              @update:model-value="toggleAll"
            />
          </th>
          <th v-for="column in columns" :key="column.key" :style="columnStyle(column)">
            {{ column.label }}
          </th>
          <th v-if="$slots.actions" class="table__actions-head"><span class="sr-only">{{ uiLabel('actions') }}</span></th>
        </tr>
      </thead>

      <tbody>
        <!--
          Loading draws the table it is about to become: the same columns, the
          same row height, the same widths. Nothing shifts when the data lands,
          which is the difference between a page that settles and one that
          jumps under the cursor.
        -->
        <!--
          Y solo cuando la carga TARDA (useDelayedLoading, 150 ms): antes de
          eso las filas de mentira están, guardando el sitio, pero invisibles.
          Una lista que llega en 60 ms pasa de nada a sus filas sin destellar
          gris; y el «no hay nada» tampoco sale mientras se espera, que decir
          vacío antes de saberlo es mentir.

          Al RECARGAR con filas ya en pantalla (un filtro, la página siguiente)
          no se vuelve al esqueleto: se quedan las de antes, apagadas, hasta
          que llegan las nuevas (`table-wrap--refreshing`).
        -->
        <template v-if="placeholder">
          <tr
            v-for="row in skeletonRows"
            :key="`skeleton-${row}`"
            class="table__ghost"
            :class="{ 'table__ghost--waiting': !skeletonShown }"
          >
            <td v-if="selectable" class="table__tick"><UiSkeleton width="18px" /></td>
            <td
              v-for="column in columns"
              :key="column.key"
              :class="cellClass(column)"
              :data-label="column.label"
            >
              <UiSkeleton :width="skeletonWidth(column, row)" />
            </td>
            <td v-if="$slots.actions" class="table__actions"><UiSkeleton width="60px" /></td>
          </tr>
        </template>

        <tr v-else-if="!rows.length" class="table__arrive">
          <td :colspan="colspan" class="table__state">
            <slot name="empty">{{ emptyText ?? uiLabel('empty') }}</slot>
          </td>
        </tr>

        <tr
          v-else
          v-for="(row, index) in rows"
          :key="row.id ?? index"
          class="table__arrive"
          :class="{ 'table__row--clickable': clickable, 'table__row--picked': isPicked(row) }"
          @click="clickable && $emit('rowClick', row)"
        >
          <!-- `.stop`, or ticking a box on a clickable table also opens the row
               that was ticked — which is the one thing somebody choosing six of
               them definitely did not ask for. -->
          <td v-if="selectable" class="table__tick" @click.stop>
            <UiCheckbox
              :model-value="isPicked(row)"
              :aria-label="rowLabel(row)"
              @update:model-value="toggle(row)"
            />
          </td>
          <!-- data-label carries the column heading down into the cell, which
               is what lets the mobile layout below print it as the row's own
               label once the header is gone. -->
          <td
            v-for="column in columns"
            :key="column.key"
            :class="cellClass(column)"
            :data-label="column.label"
          >
            <!-- The dash says «no value» under a heading. A column with no
                 heading (the row's buttons) has nothing to say when a row has
                 no button — and in the phone card that lone «—» sat in the
                 middle of every card, under nothing. A slot whose v-if is false
                 renders only a comment, which Vue treats as empty: this
                 fallback is what used to print the dash. -->
            <slot :name="`cell:${column.key}`" :row="row" :value="row[column.key]">
              {{ row[column.key] ?? (column.label ? '—' : '') }}
            </slot>
          </td>
          <td v-if="$slots.actions" class="table__actions">
            <slot name="actions" :row="row" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import UiSkeleton from './UiSkeleton.vue'
import UiCheckbox from './UiCheckbox.vue'
import { uiLabel } from '../utils/labels'
import { useDelayedLoading } from '../composables/useDelayedLoading'

const props = defineProps({
  // [{ key, label, width, align, hideOnPhone }]. `hideOnPhone` drops the cell
  // from the card layout below: for a column whose value is worth a column on
  // a laptop but not a line of its own on a 390px screen.
  columns: { type: Array, required: true },
  rows: { type: Array, default: () => [] },
  loading: Boolean,
  clickable: Boolean,
  // Left undefined on purpose: the shared wording is used when a caller has
  // nothing more specific to say, and that wording is translated.
  emptyText: String,
  // How many placeholder rows to draw. Roughly what the caller expects to get
  // back, so the page does not visibly grow or shrink when it arrives.
  skeletonRows: { type: Number, default: 5 },

  // ── Choosing several rows to do one thing to ───────────────────────────────
  //
  // Off by default, and every list that does not ask for it is untouched.
  //
  // It is here rather than in the first screen that wanted it because it is the
  // same four decisions everywhere — a box per row, a box in the header that
  // knows about «some», not opening the row you just ticked, and a selection
  // that survives a re-render — and the second screen to need them would have
  // got three of the four right.
  //
  // The selection is a list of ids and belongs to the CALLER: the table draws
  // it and reports changes. It has to, because what «all» means outlives this
  // component — a page turn, a filter, a reload — and only the screen knows
  // whether the ones it no longer shows are still chosen.
  selectable: Boolean,
  selected: { type: Array, default: () => [] },
  // What a screen reader hears on a row's box. The row's own name, not "row 4".
  rowLabelKey: { type: String, default: 'title' },
})
const emit = defineEmits(['rowClick', 'update:selected'])

// ── Cuándo se ven las filas de mentira ──────────────────────────────────────
//
// `skeletonShown`: la carga ya lleva 150 ms (y, una vez visibles, se quedan al
// menos 300: ver useDelayedLoading). `placeholder`: hay que guardar el sitio
// con filas de mentira, visibles o no. Es la primera carga (sin filas) o el
// final de una que se llegó a ver y aún no ha cumplido su mínimo — y solo esa:
// una RECARGA con filas no vuelve al esqueleto al acabar.
const { visible: skeletonShown } = useDelayedLoading(() => props.loading)
const wasSkeleton = ref(false)
const placeholder = computed(() =>
  (props.loading && !props.rows.length) || (!props.loading && skeletonShown.value && wasSkeleton.value))
watch([placeholder, skeletonShown], ([shown, delayed]) => { wasSkeleton.value = shown && delayed })
const refreshing = computed(() => props.loading && props.rows.length > 0 && skeletonShown.value)

const slots = defineSlots()
const colspan = computed(() =>
  props.columns.length + (slots.actions ? 1 : 0) + (props.selectable ? 1 : 0))

// ── The selection ────────────────────────────────────────────────────────────

const picked = computed(() => new Set(props.selected.map(String)))

const isPicked = (row) => picked.value.has(String(row.id))
const rowLabel = (row) => String(row[props.rowLabelKey] ?? row.id ?? '')

/** «All» means the rows on screen, which is the only set anybody can see. */
const allPicked = computed(() =>
  props.rows.length > 0 && props.rows.every(isPicked))
const somePicked = computed(() => props.rows.some(isPicked))

function toggle(row) {
  const next = new Set(picked.value)
  const id = String(row.id)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  emit('update:selected', [...next])
}

/**
 * The header box: all of them, or none of them.
 *
 * It only ever touches what is on screen. A page of 25 out of 300 that ticked
 * all 300 would be a button whose consequence nobody can see, and the answer to
 * «which ones did I just complete» would be a number rather than a list.
 */
function toggleAll() {
  const next = new Set(picked.value)
  if (allPicked.value) for (const row of props.rows) next.delete(String(row.id))
  else for (const row of props.rows) next.add(String(row.id))
  emit('update:selected', [...next])
}

// Width and alignment live on the header cell only. The body used to carry them
// inline as well, and an inline `width: 220px` is not something the mobile card
// layout below can undo — a class can.
const columnStyle = (column) => ({
  ...(column.width ? { width: column.width } : {}),
  ...(column.align ? { textAlign: column.align } : {}),
})

const cellClass = (column) => [
  column.align ? `table__cell--${column.align}` : null,
  column.hideOnPhone ? 'table__cell--desktop' : null,
]

/**
 * Every column but one has stood down for the phone, so there is nothing left
 * to stack: the row goes back to being a line instead of a card with a single
 * fact in it and a separator that separates nothing.
 */
const tight = computed(() =>
  props.columns.filter(column => !column.hideOnPhone).length === 1)

// ── Cards when the table does not fit ────────────────────────────────────────
//
// On a phone, always. Anywhere else, when the table's natural width is more
// than its box: measured in table mode, and the answer applied before the
// browser paints (both happen in the same tick), so there is no flash of the
// other layout. It is measured again when the box changes width and when the
// rows or the columns change, which is when the natural width moves.
const wrap = ref(null)
const tableEl = ref(null)
const PHONE = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 720px)') : null
const cards = ref(Boolean(PHONE?.matches))

let measuring = false
async function decide() {
  if (measuring || !wrap.value || !tableEl.value) return
  if (PHONE?.matches) { cards.value = true; return }
  measuring = true
  try {
    cards.value = false
    await nextTick()
    const box = wrap.value?.clientWidth ?? 0
    const natural = tableEl.value?.scrollWidth ?? 0
    cards.value = box > 0 && natural > box + 1
  } finally {
    measuring = false
  }
}

let observer = null
let lastWidth = -1
onMounted(() => {
  decide()
  if (typeof ResizeObserver !== 'undefined' && wrap.value) {
    observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0)
      if (width === lastWidth) return
      lastWidth = width
      decide()
    })
    observer.observe(wrap.value)
  }
  PHONE?.addEventListener?.('change', decide)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  PHONE?.removeEventListener?.('change', decide)
})
watch(() => [props.rows, props.columns, props.loading], () => nextTick(decide))

// Uneven widths, stable per cell. Identical bars in a perfect grid look like a
// rendering bug; varied ones read as text that has not loaded.
const WIDTHS = ['86%', '64%', '92%', '58%', '78%', '70%']
function skeletonWidth(column, row) {
  const index = props.columns.indexOf(column)
  return WIDTHS[(index * 3 + row * 2) % WIDTHS.length]
}
</script>

<style scoped>
/* The wrapper scrolls, not the page: a wide table must never push the layout
   sideways on a laptop screen.

   `position: relative` is what makes that true. Without it the wrapper is not
   the containing block for the absolutely positioned .sr-only labels inside the
   table, so overflow never clipped them: on a tablet they sat 17px past the
   right edge of a table that was itself wider than the screen, and the window
   grew a horizontal scrollbar of its own for exactly those 17px. */
.table-wrap { position: relative; width: 100%; overflow-x: auto; }

.table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }

/* The horizontal padding matches a card's body, so a flush table lines up with
   the text of the card above it instead of sitting four pixels to its left. */
.table th {
  padding: var(--space-3) var(--space-5);
  text-align: left;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-text-soft);
  background: var(--color-surface-2);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
}

/* Roomier rows. An ERP table is read all day, and rows packed to the line
   height make two adjacent records look like one. */
.table td {
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
  vertical-align: middle;
}

.table tbody tr:last-child td { border-bottom: none; }
.table tbody tr:hover td { background: var(--color-gray-50); }

.table__row--clickable { cursor: pointer; }

/* Las filas de mentira antes del umbral: ocupan su sitio y no se ven. */
.table__ghost--waiting { visibility: hidden; }

/* En la ficha del teléfono el rótulo se come el 38 % de la celda, y la barra
   de mentira medía su porcentaje contra la celda entera: se salía por la
   derecha de la tarjeta. Lo que queda al lado del rótulo es su techo. */
.table-wrap--cards .table__ghost td:not(:first-child) :deep(.skeleton) {
  max-width: calc(62% - var(--space-3));
}

/* Lo de antes mientras llega lo nuevo: apagado y sin recibir toques, que tocar
   una fila que está a punto de cambiar abre la que ya no está. */
.table-wrap--refreshing tbody { opacity: 0.55; pointer-events: none; transition: opacity var(--fast); }

/* Las filas de verdad entran con un fundido corto al llegar. Solo opacidad: un
   desplazamiento haría temblar una tabla de cuarenta filas. Al reordenar o
   filtrar, Vue reutiliza las filas por su id y no las vuelve a fundir. */
.table__arrive { animation: table-arrive 180ms ease-out; }
@keyframes table-arrive { from { opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .table__arrive { animation: none; }
}

/* El toque con el dedo (la receta de `.tap` en base.css, aquí por celda
   porque el fondo de una fila de tabla lo pintan sus `td`). Contesta en el
   momento del toque, antes de que llegue la ficha. */
@media (pointer: coarse) {
  .table__row--clickable td { transition: background-color 120ms ease; }
  .table tbody tr.table__row--clickable:active td { background: var(--color-gray-100); }
  .table-wrap--cards .table tbody tr.table__row--clickable { transition: background-color 120ms ease; }
  .table-wrap--cards .table tbody tr.table__row--clickable:active { background: var(--color-gray-100); }
  .table-wrap--cards .table tbody tr.table__row--clickable:active td { background: none; }
}

.table__state {
  padding: var(--space-8) var(--space-4) !important;
  text-align: center;
  color: var(--color-text-mute);
}

.table__cell--right  { text-align: right; }
.table__cell--center { text-align: center; }

.table__actions { text-align: right; white-space: nowrap; }
.table__actions-head { width: 1%; }

/* The tick column takes only what it needs and never grows: it is furniture,
   not a column of data. */
.table__tick, .table__tick-head { width: 1%; padding-right: 0 !important; }
.table__tick-head { padding-block: var(--space-2) !important; }
.table__row--picked td { background: var(--color-primary-soft); }
.table tbody tr.table__row--picked:hover td { background: var(--color-primary-soft); }

/* ── Phone ───────────────────────────────────────────────────────────────────
   A table stops being a table. Six columns on a 390px screen means swiping
   sideways to read one record, and you lose the heading the moment you do —
   so each row becomes a card and every cell carries its own label.

   Done in CSS over the same markup rather than with a second template: the
   callers' `#cell:` slots, the skeletons and the empty state all keep working,
   and there is no second layout to keep in step with the first.
*/
/* The card layout below used to hang off `@media (max-width: 720px)`, the
   width of the WINDOW. A table does not care about the window, it cares about
   its own box: at 768 px (a tablet, a phone on its side) the lists were tables
   again and slid sideways — 393 px for users, 406 for works, 164 for quotes.
   The class is set by the script when the table does not fit in its box, and
   always on a phone. */
.table-wrap--cards .table,
.table-wrap--cards .table tbody,
.table-wrap--cards .table tr,
.table-wrap--cards .table td { display: block; width: 100%; }
.table-wrap--cards .table thead { display: none; }

.table-wrap--cards .table tbody tr {
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
.table-wrap--cards .table tbody tr:last-child { border-bottom: none; }
.table-wrap--cards .table tbody tr:hover td { background: none; }

.table-wrap--cards .table td {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  padding: 0;
  border: none;
  /* Undoes any per-column alignment: in a card everything reads from the
     left, against its label. */
  text-align: left;
  /* A card is exactly as wide as the screen, and nothing inside may push it
     wider. Measured at 360 px: an email in a customer card
     («administracion@textilesvillaverde.com») and an audit action code
     («purchasing.supplier_archived») are single words, and a flex cell cannot
     shrink below its longest word — the list grew a sideways scroll of 7 to
     20 px for them. `anywhere` lowers that floor; the rule below lets the
     value's own box shrink with it. */
  overflow-wrap: anywhere;
}
/* `:deep`: what sits in a cell is the caller's slot, scoped to the caller. */
.table-wrap--cards .table td > :deep(*) { min-width: 0; }
.table-wrap--cards .table td + td { margin-top: var(--space-2); }

.table-wrap--cards .table td::before {
  content: attr(data-label);
  flex: 0 0 38%;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-soft);
}
/* A column declared with `label: ''` (a row's own buttons, kept as a column
   so it can sit anywhere in the row) had the same problem the actions cell
   had: an empty label box 38% wide, so «Emitir» or «Anular» hung off the
   middle of the card. No label, no box; and no cell at all when the row has
   nothing to put there. */
.table-wrap--cards .table td[data-label='']::before,
.table-wrap--cards .table td:not([data-label])::before { content: none; }
.table-wrap--cards .table td[data-label='']:empty,
.table-wrap--cards .table td:not([data-label]):empty { display: none; }
/* The first column is the record's name: it gets the whole width and no
   label, the way the top line of a card should read. */
.table-wrap--cards .table td:first-child::before { content: none; }
.table-wrap--cards .table td:first-child { font-size: var(--text-md); }

/* `td.` and not the bare class, in every rule below, and that IS the fix:
   `.table td` up there is one specificity point heavier than
   `.table__actions`, so the margin, the padding and the border were all
   being overridden by it and the dashed separator never drew a single
   pixel. `.table td::before` won the same way, which is the expensive half:
   the actions cell has no `data-label`, so it grew an EMPTY label box 38%
   wide and every row's buttons hung off the middle of the card instead of
   its right edge. Three dead declarations and one that was actively lying. */
.table-wrap--cards .table td.table__actions {
  align-items: center;
  justify-content: flex-end;
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px dashed var(--color-border);
  white-space: normal;
}
.table-wrap--cards .table td.table__actions::before { content: none; }

/* A column the caller marked `hideOnPhone`. It is worth a column on a laptop
   and not a line of its own here: five rows of metadata under every file
   turn a folder of twenty into a screen and a half of scrolling. */
.table-wrap--cards .table td.table__cell--desktop { display: none; }

/* ── The tick, once the row is a card ──────────────────────────────────────
   Out of the flow and into the top corner. Left in the flow it would be a
   line of its own reading "▢" with nothing beside it, above the name — and
   it is furniture, not a fact about the record.

   The corner is also where a thumb already is on a list somebody is going
   down ticking things off. */
.table-wrap--cards .table tbody tr:has(.table__tick) { position: relative; }

.table-wrap--cards .table td.table__tick {
  position: absolute;
  top: var(--space-3);
  right: var(--space-4);
  width: auto;
  margin: 0;
  padding: 0;
}

/* And with a tick in front of it, the record's name is the SECOND cell. All
   three rules that make the top line of a card read as a title were keyed to
   `:first-child`, which is now the box. */
.table-wrap--cards .table td.table__tick + td::before { content: none; }
.table-wrap--cards .table td.table__tick + td {
  margin-top: 0;
  font-size: var(--text-md);
  /* Clear of the box, or a long title runs under it. */
  padding-right: 32px;
}

/* And when only ONE column is left standing, the row stops being a card and
   goes back to being a line: the name on the left, the menu on the right.
   That is the shape every file manager on a phone has, and it is the honest
   one — a card holding a single fact, with a dashed rule under it separating
   that fact from nothing, is a card in name only. */
.table-wrap--cards .table.table--tight tbody tr {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.table-wrap--cards .table.table--tight td { flex: 1; min-width: 0; }
.table-wrap--cards .table.table--tight td::before { content: none; }
.table-wrap--cards .table.table--tight td.table__actions {
  flex: 0 0 auto;
  width: auto;
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.table-wrap--cards /* "Nothing here" is not a record,
.table-wrap--cards so it is not a card: no label,
.table-wrap--cards no padding
   from the row around it,
.table-wrap--cards still centred. Same trap as the actions cell —
   without `td.` this lost `display: block` to `.table td { display: flex }`
   and the empty state was squeezed into the right 62% of the card. */
.table-wrap--cards .table tbody tr:has(.table__state) { padding: 0; }
.table-wrap--cards .table td.table__state {
  display: block;
  text-align: center;
  padding: var(--space-8) var(--space-4) !important;
}
.table-wrap--cards .table td.table__state::before { content: none; }

</style>
